import { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import { useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  parseDuplicateOf,
  rowId,
  selectCatalogueRows,
} from "../helpers/fetcherCatalogue";
import { formatDisplayDateTime } from "../helpers/formatters";

interface Props {
  open: boolean;
  configId: string;
  onClose: () => void;
  onSwitchToEdit: () => void;
  onDuplicate: (configId: string) => void;
  onShowBrands: (configId: string) => void;
  /** opens the twin named in the duplicate banner */
  onOpenTwin: (configId: string) => void;
  canEdit: boolean;
}

const SECTION_LABEL_SX = {
  fontSize: "11px",
  letterSpacing: "0.5px",
  textTransform: "uppercase" as const,
  color: "#00000099",
  fontWeight: 700,
};

/** A disabled select, so the recipe fields keep their chevron while read only. */
const ReadOnlySelect = ({
  label,
  value,
  helperText,
}: {
  label: string;
  value: string;
  helperText?: string;
}) => (
  <TextField
    select
    fullWidth
    required
    size="small"
    label={label}
    value={value}
    disabled
    helperText={helperText}
    color="secondary"
  >
    <MenuItem value={value}>{value}</MenuItem>
  </TextField>
);

/**
 * The read-only config detail. Everything on it comes straight off the
 * catalogue row - the lookup *names* are delivered alongside their ids, so
 * nothing has to be resolved against the lookup tables to show it.
 */
const ViewConfigModal = ({
  open,
  configId,
  onClose,
  onSwitchToEdit,
  onDuplicate,
  onShowBrands,
  onOpenTwin,
  canEdit,
}: Props) => {
  const { fetcherSettingsMainTablesData } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  const configRows = useMemo(
    () => selectCatalogueRows(fetcherSettingsMainTablesData, "config"),
    [fetcherSettingsMainTablesData],
  );

  const config = useMemo(
    () => configRows.find((row) => rowId(row.item_id) === configId),
    [configRows, configId],
  );

  const twins = useMemo(() => {
    if (!config) return [];
    return parseDuplicateOf(config.duplicate_of).map((id) => ({
      id,
      name:
        configRows.find((row) => rowId(row.item_id) === id)?.item_name ?? "",
    }));
  }, [config, configRows]);

  if (!config) return null;

  const usedByBrands = config.used_by_brands ?? 0;
  const reportLabel = config.report_id
    ? `${config.report_name ?? "-"} (${rowId(config.report_id)})`
    : "-";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "880px", maxWidth: "94vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>View Config</DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
        {config.item_name} ({rowId(config.item_id)}) · modified{" "}
        {formatDisplayDateTime(config.modified)}
      </Typography>
      <Divider />

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 3,
          py: 1.5,
          backgroundColor: "#FAFAFC",
          borderBottom: "1px solid #0000001F",
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {usedByBrands}
          </Typography>
          {usedByBrands > 0 ? (
            <Link
              component="button"
              underline="always"
              color="secondary"
              sx={{ fontSize: "12px" }}
              onClick={() => onShowBrands(rowId(config.item_id))}
            >
              brands - view list
            </Link>
          ) : (
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              brands
            </Typography>
          )}
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
            Interface
          </Typography>
          <Chip
            size="small"
            color="secondary"
            label={config.interface_name ?? "-"}
          />
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
            Report
          </Typography>
          <Typography sx={{ fontSize: "13.5px" }}>
            {config.report_name ?? "-"}
          </Typography>
        </Stack>
      </Stack>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {twins.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <b>Blocked - 100% duplicate.</b>{" "}
            {twins.map((twin, index) => (
              <Box component="span" key={twin.id}>
                {index > 0 && ", "}
                <Link
                  component="button"
                  underline="always"
                  color="inherit"
                  sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
                  onClick={() => onOpenTwin(twin.id)}
                >
                  {twin.name ? `${twin.name} (${twin.id})` : twin.id}
                </Link>
              </Box>
            ))}{" "}
            already {twins.length === 1 ? "has" : "have"} this exact recipe.
          </Alert>
        )}

        <Stack spacing={2.5}>
          <Typography sx={SECTION_LABEL_SX}>Identity</Typography>

          <TextField
            fullWidth
            required
            size="small"
            label="Configuration Name"
            value={config.item_name ?? ""}
            disabled
            helperText="Max 100 chars."
            color="secondary"
          />

          <Typography sx={SECTION_LABEL_SX}>Recipe</Typography>

          <TextField
            fullWidth
            required
            size="small"
            label="Report"
            value={reportLabel}
            disabled
            helperText="The report is fixed once a config exists — changing it would repoint every connected brand."
            color="secondary"
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <ReadOnlySelect
                label="Collection Method"
                value={config.collection_method ?? ""}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ReadOnlySelect
                label="Sub-Filtering Method"
                value={config.sub_filtering_method ?? ""}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ReadOnlySelect
                label="Format Type"
                value={config.format_type ?? ""}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Collection Type"
                value={config.collection_type ?? ""}
                disabled
                helperText="Inherited from the report."
                color="secondary"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Chip size="small" label="Read only" />
        <Box sx={{ flex: 1 }} />
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
        {canEdit && (
          <>
            <Button
              color="secondary"
              onClick={() => onDuplicate(rowId(config.item_id))}
              disabled
            >
              Duplicate
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSwitchToEdit}
              disabled
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewConfigModal;
